import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { GsEvent } from '@/lib/types';

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function CalendarMonthGrid({ events }: { events: GsEvent[] }) {
  const today = new Date();
  const [cursor, setCursor] = useState({ y: today.getFullYear(), m: today.getMonth() });

  const byDay = useMemo(() => {
    const map = new Map<number, GsEvent[]>();
    events.forEach((e) => {
      if (!e.event_date) return;
      const d = new Date(`${e.event_date}T00:00:00`);
      if (d.getFullYear() !== cursor.y || d.getMonth() !== cursor.m) return;
      const day = d.getDate();
      if (!map.has(day)) map.set(day, []);
      map.get(day)!.push(e);
    });
    return map;
  }, [events, cursor]);

  const first = new Date(cursor.y, cursor.m, 1);
  const startPad = first.getDay();
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const cells: (number | null)[] = [
    ...Array.from({ length: startPad }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const label = first.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const move = (delta: number) => {
    const d = new Date(cursor.y, cursor.m + delta, 1);
    setCursor({ y: d.getFullYear(), m: d.getMonth() });
  };
  const isToday = (day: number) => today.getFullYear() === cursor.y && today.getMonth() === cursor.m && today.getDate() === day;

  return (
    <div className="border border-background-200 rounded-lg overflow-hidden bg-background-50">
      <div className="flex items-center justify-between gap-3 px-4 md:px-5 py-4 border-b border-background-200">
        <h3 className="font-heading font-extrabold uppercase text-2xl md:text-3xl text-foreground-950 leading-none">{label}</h3>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => move(-1)} aria-label="Previous month" className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300 hover:border-foreground-700 cursor-pointer text-foreground-800">
            <i className="ri-arrow-left-s-line text-lg"></i>
          </button>
          <button type="button" onClick={() => setCursor({ y: today.getFullYear(), m: today.getMonth() })} className="font-label font-semibold uppercase tracking-wide text-xs px-3 h-9 rounded-md border border-background-300 hover:border-foreground-700 cursor-pointer text-foreground-800">
            Today
          </button>
          <button type="button" onClick={() => move(1)} aria-label="Next month" className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300 hover:border-foreground-700 cursor-pointer text-foreground-800">
            <i className="ri-arrow-right-s-line text-lg"></i>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 border-b border-background-200 bg-background-100">
        {DOW.map((d) => (
          <div key={d} className="px-2 py-2 text-center font-label uppercase tracking-widest text-[10px] md:text-[11px] text-foreground-600">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {cells.map((day, i) => (
          <div key={i} className={`min-h-[84px] md:min-h-[112px] border-r border-b border-background-200 p-1.5 md:p-2 ${day ? '' : 'bg-background-100/60'}`}>
            {day && (
              <>
                <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full font-label text-xs ${isToday(day) ? 'bg-accent-500 text-foreground-950 font-bold' : 'text-foreground-600'}`}>{day}</span>
                <div className="mt-1 flex flex-col gap-1">
                  {(byDay.get(day) || []).slice(0, 3).map((e) => (
                    <Link key={e.id} to={`/events/${e.slug}`} title={e.name} className="block bg-primary-100 hover:bg-primary-200 text-primary-900 rounded-sm px-1.5 py-1 text-[10px] md:text-[11px] leading-tight truncate cursor-pointer">
                      {e.name}
                    </Link>
                  ))}
                  {(byDay.get(day) || []).length > 3 && (
                    <span className="text-[10px] text-foreground-500 px-1">+{(byDay.get(day) || []).length - 3} more</span>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {byDay.size === 0 && (
        <p className="px-5 py-6 text-center text-foreground-600 text-sm">No tournaments scheduled this month. Try another month or switch to the list view.</p>
      )}
    </div>
  );
}